import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, Link } from "wouter";

import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import ToggleButton from 'react-bootstrap/ToggleButton';

import { absURL, quizTypes } from '../utils';
import QuizApi from '../QuizApi';
import AdminHeader from './AdminHeader';
import InfoTooltip from './InfoTooltip';
import FileUploadSingle from './FileUploadSingle';
import DeezerSearch from './DeezerSearch';

export default function AdminQuizCreate(props) {

  const [quizName, setQuizName] = useState("");
  const [quizDuration, setQuizDuration] = useState(10);
  const [quizType, setQuizType] = useState(quizTypes[0].value);
  const [horizontalScroll, setHorizontalScroll] = useState(false);
  const [freeTextResultMode, setFreeTextResultMode] = useState("fadeOut");
  const [singleQuestionMode, setSingleQuestionMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setLocation] = useLocation();
  const [displayError, setDisplayError] = useState(false);
  const [submitErrorMsg, setSubmitErrorMsg] = useState("");
  const [useQuizGen, setUseQuizGen] = useState(false);
  const [quizGenFile, setQuizGenFile] = useState(false);
  const [quizGroups, setQuizGroups] = useState([]);
  const [quizGroupId, setQuizGroupId] = useState(null);
  const [lobbyMusic, setLobbyMusic] = useState(null);
  const [competitionMode, setCompetitionMode] = useState(false);

  useEffect(() => {
    if (useQuizGen) {
      setQuizType("Quiz");
    }
  }, [useQuizGen]);

  useEffect(() => {
    QuizApi.quizGroupList().then(result => {
      setQuizGroups(result?.response?.groups ?? []);
    });
  }, []);

  // =======================
  // Form submit callback
  // =======================
  const onSubmit = useCallback(ev => {

    ev.preventDefault();

    const fn = async () => {

      if (!(quizName || "").trim()) {
        setDisplayError(true);
        return;
      }

      setLoading(true);

      const quizConfig = {
        horizontalScroll,
        ...(quizType === "Free" ? { freeTextResultMode } : {}),
        ...(quizType === "Quiz" ? { singleQuestionMode, competitionMode } : {}),
        lobbyMusic: lobbyMusic || null
      };

      let result = null;
      try {
        result = await QuizApi.quizCreate(
          quizName, quizDuration, quizType, quizConfig,
          useQuizGen ? quizGenFile : null,
          quizGroupId
        );
      } catch (e) {
        setDisplayError(true);
        setSubmitErrorMsg(e.message);
      }

      setLoading(false);

      if (result && result.success && result.response && result.response.quizId) {
        setLocation(absURL(`/admin/quiz/${result.response.quizId}/questions`));
      }
      else {
        setDisplayError(true);
        setSubmitErrorMsg(
          result && result.error === "free_tier_limit"
            ? "Gratiskontot tillåter max 10 quizzar. Uppgradera för att skapa fler."
            : "Det uppstod ett fel när vi försökte skapa quizzen."
        );
      }
    };

    fn();

  }, [quizName, quizDuration, quizType, horizontalScroll, freeTextResultMode, singleQuestionMode, competitionMode, setLocation, useQuizGen, quizGenFile, quizGroupId, lobbyMusic]);

  return (
    <div id="adminQuizCreate">
      <div>

        {loading && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" />
          </div>
        )}

        {!loading &&
          <div>
            <AdminHeader />
            <div className="adminContentWrapper">
              <div style={{ padding: "20px", maxWidth: "300px" }}>

                <div>
                  <Button variant="outline-secondary" size="sm" onClick={() => window.history.back()}>
                    Tillbaka
                  </Button>

                  {/* Quizgeneratorn (PDF/textfil → auto-genererade frågor) används inte just nu.
                  <ToggleButton
                    className="ml-1 mt-2"
                    type="checkbox"
                    variant="outline-secondary"
                    size="sm"
                    checked={useQuizGen}
                    value="1"
                    onChange={(e) => setUseQuizGen(e.currentTarget.checked)}
                  >
                    &nbsp;Använd quizgeneratorn
                  </ToggleButton>
                  */}
                </div>

                <form onSubmit={onSubmit} className="mt-3">

                  <div className="form-group">
                    <label htmlFor="quizType">Quiztyp:</label>
                    <InfoTooltip placement="left" className="ml-1">
                      <b>Quiz</b>
                      <p>Tävlingsformen där man svarar på frågor och får poäng.</p>

                      <b>Undersökning</b>
                      <p>(Poll) En eller flera undersökningsfrågor där man i resultatet ser fördelning av svar.</p>

                      <b>Fritext</b>
                      <p>En eller flera frågor där varje deltagare kan skicka in fritext (flera gånger). Resultat visas som rullande lista/or.</p>

                      <b>Music quiz</b>
                      <p>Gissa låt! Varje fråga kopplas till ett musikklipp från Deezer (30 sek). Deltagarna gissar artist, låt eller år.</p>
                    </InfoTooltip>
                    <select
                      name="quizType"
                      className="form-control"
                      disabled={useQuizGen}
                      style={{ cursor: useQuizGen ? "not-allowed" : "pointer" }}
                      onChange={ev => setQuizType(ev.target.value)}>
                      {quizTypes.map(qt =>
                        <option
                          key={qt.value}
                          selected={qt.value === quizType}
                          value={qt.value}>
                          {qt.text}
                        </option>)
                      }
                    </select>
                  </div>

                  <div className="form-group mt-2">
                    <label htmlFor="quizGroup">Grupp:</label>
                    <select
                      id="quizGroup"
                      className="form-control"
                      value={quizGroupId || ""}
                      onChange={ev => setQuizGroupId(ev.target.value || null)}>
                      <option value="">Ingen grupp</option>
                      {quizGroups.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group mt-2">
                    <label htmlFor="quizName">Quizzens namn:</label>
                    <input
                      type="text"
                      id="quizName"
                      className="form-control"
                      value={quizName}
                      onChange={ev => setQuizName(ev.target.value)} />
                  </div>

                  {/* Quizgeneratorn används inte just nu, se kommentar vid togglen ovan.
                  {useQuizGen &&
                    <div className="form-group">
                      <label>Fil till quizgeneratorn:</label>
                      <FileUploadSingle
                        mimeTypeAcceptList={[".pdf", ".txt"]}
                        onFileChanged={newFile => setQuizGenFile(newFile)}
                      />
                    </div>
                  }
                  */}

                  {displayError && !(quizName || "").trim() && <Alert variant={"danger"}>
                    Du måste ange ett quiznamn
                  </Alert>
                  }

                  <div className="form-group">
                    <label htmlFor="quizDuration">Tid (minuter):</label>
                    <InfoTooltip placement="left" className="ml-1">
                      <p>Tid på sig för att slutföra. När tiden runnit ut så stängs quizzen automatiskt och resultat visas. Tiden går att starta om under körning.</p>
                    </InfoTooltip>
                    <input
                      type="number"
                      id="quizDuration"
                      className="form-control"
                      value={quizDuration}
                      onChange={ev => setQuizDuration(ev.target.value)} />
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="horizontalScroll"
                      id="horizontalScroll"
                      onChange={ev => setHorizontalScroll(ev.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="horizontalScroll">
                      Horisontell scroll (swipe)
                    </label>
                  </div>

                  {quizType === "Free" &&
                    <div className="form-group mt-3">
                      <label>Visning av fritext-svar:</label>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="freeTextResultMode"
                          id="freeTextResultModeFadeOut"
                          value="fadeOut"
                          checked={freeTextResultMode === "fadeOut"}
                          onChange={() => setFreeTextResultMode("fadeOut")}
                        />
                        <label className="form-check-label" htmlFor="freeTextResultModeFadeOut">
                          Tona ut svar (standard)
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="freeTextResultMode"
                          id="freeTextResultModeKeepAll"
                          value="keepAll"
                          checked={freeTextResultMode === "keepAll"}
                          onChange={() => setFreeTextResultMode("keepAll")}
                        />
                        <label className="form-check-label" htmlFor="freeTextResultModeKeepAll">
                          Behåll alla svar (scrollbar lista)
                        </label>
                      </div>
                    </div>
                  }

                  {quizType === "Quiz" &&
                    <div className="form-check mt-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="singleQuestionMode"
                        id="singleQuestionMode"
                        checked={singleQuestionMode}
                        onChange={ev => setSingleQuestionMode(ev.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="singleQuestionMode">
                        Visa resultat som fördelningsdiagram
                        <InfoTooltip placement="left" className="ml-1">
                          <p>Istället för "% rätt per fråga" visas en stapel per svarsalternativ med markering av rätt svar — bra för att se hur gruppen resonerade.</p>
                        </InfoTooltip>
                      </label>
                    </div>
                  }

                  {quizType === "Quiz" &&
                    <div className="form-check mt-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="competitionMode"
                        id="competitionMode"
                        checked={competitionMode}
                        onChange={ev => setCompetitionMode(ev.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="competitionMode">
                        Tävlingsläge
                        <InfoTooltip placement="left" className="ml-1">
                          <p>Resultatsidan visar en dynamisk topplista med alla deltagares poäng i realtid, sorterad från bäst till sämst.</p>
                        </InfoTooltip>
                      </label>
                    </div>
                  }

                  <div className="mt-3">
                    <label>Lobbymusik (spelas medan deltagarna väntar):</label>
                    {lobbyMusic && (
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        className="ml-2"
                        onClick={() => setLobbyMusic(null)}>
                        Ta bort
                      </Button>
                    )}
                    <DeezerSearch
                      label=""
                      selectedTrack={lobbyMusic}
                      onTrackSelected={track => setLobbyMusic({
                        trackId: track.id,
                        previewUrl: track.previewUrl,
                        title: track.title,
                        artist: track.artist,
                        album: track.album,
                        cover: track.cover
                      })}
                    />
                  </div>

                  <Button type="submit" className="btn mt-4 btn-quizPrimaryDark">Skapa</Button>

                  {displayError && submitErrorMsg &&
                    <Alert variant={"danger"} className="mt-4">
                      {submitErrorMsg}
                    </Alert>
                  }

                </form>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  );
};
